import { Component, computed, input } from '@angular/core';
import { injectOverlayContext } from 'ng-primitives/portal';
import { NgpTooltip, NgpTooltipArrow } from 'ng-primitives/tooltip';

/** Content rendered inside the bubble when a tooltip is opened by `TooltipDirective`. */
export interface TooltipContent {
  /** Primary line of the tooltip. */
  readonly text: string;
  /** Supporting line rendered underneath the primary line. */
  readonly secondaryText?: string;
}

/**
 * The tooltip bubble. It is rendered inside an ng-primitives overlay, so it can only be used
 * as the content of an `ngpTooltipTrigger` (which is what `TooltipDirective` does for you).
 */
@Component({
  selector: 'joii-tooltip',
  imports: [NgpTooltipArrow],
  hostDirectives: [NgpTooltip],
  host: {
    '[attr.data-has-secondary-text]': 'secondaryLine() ? "" : null',
  },
  template: `
    <span class="tooltip__text">{{ primaryLine() }}<ng-content /></span>
    @if (secondaryLine()) {
      <span class="tooltip__secondary-text">{{ secondaryLine() }}</span>
    }
    <span class="tooltip__beak" ngpTooltipArrow aria-hidden="true">
      <svg class="tooltip__beak-shape" width="16" height="11" viewBox="0 0 16 11" fill="none" focusable="false">
        <path
          d="M6.35688 9.65965C7.14873 10.5168 8.50325 10.5168 9.2951 9.65965L15.1171 3.3571C16.3004 2.07614 15.3919 0 13.648 0H2.00395C0.260087 0 -0.648458 2.07614 0.534842 3.3571L6.35688 9.65965Z"
        />
      </svg>
    </span>
  `,
  styleUrl: './tooltip.component.css',
})
export class TooltipComponent {
  /** Primary line. Falls back to the text passed by the trigger directive. */
  readonly text = input<string>('');
  /** Supporting line. Falls back to the secondary text passed by the trigger directive. */
  readonly secondaryText = input<string>('');

  private readonly context = injectOverlayContext<TooltipContent | undefined>();

  protected readonly primaryLine = computed(() => this.text() || this.context()?.text || '');
  protected readonly secondaryLine = computed(() => this.secondaryText() || this.context()?.secondaryText || '');
}
